import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Select,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Spin,
} from "antd";
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import apiClient from '../../services/apiClient';
import "./AdminPages.css";

const { Option } = Select;

/**
 * Admin tool for managing bond cashflow (migrated rows) with
 * PDF upload + AI extraction and side‑by‑side comparison.
 *
 * This mirrors the UX pattern of IssuerFinancialReportsAdmin
 * but focuses on ISIN‑level cashflow rows.
 */
const BondCashflowAdmin = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const botsApiUrl =
    process.env.REACT_APP_BOTS_API_URL ||
    process.env.REACT_APP_POC_BOTS_URL ||
    "http://localhost:5002";
  const token = localStorage.getItem("token");

  const [issuerOptions, setIssuerOptions] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [isinOptions, setIsinOptions] = useState([]);
  const [selectedIsin, setSelectedIsin] = useState(null);

  const [cashflowData, setCashflowData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [form] = Form.useForm();

  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractedModalVisible, setExtractedModalVisible] = useState(false);
  const [extractedRows, setExtractedRows] = useState([]);

  const authHeaders = useMemo(() => {
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  useEffect(() => {
    fetchIssuerOptions();
  }, []);

  useEffect(() => {
    if (selectedIssuer?.issuer) {
      fetchIsinOptions(selectedIssuer.issuer);
    } else {
      setIsinOptions([]);
      setSelectedIsin(null);
      setCashflowData([]);
    }
  }, [selectedIssuer]);

  useEffect(() => {
    if (selectedIsin) {
      fetchCashflow(selectedIsin);
    } else {
      setCashflowData([]);
    }
  }, [selectedIsin]);

  const fetchIssuerOptions = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/master-issuer/issuers/basic-industry`,
        { headers: { ...authHeaders, "Content-Type": "application/json" } }
      );
      const result = response.data;
      if (result.statusCode === 200 && Array.isArray(result.data)) {
        setIssuerOptions(result.data);
      } else {
        setIssuerOptions([]);
      }
    } catch (err) {
      console.error("Error fetching issuer options:", err);
      message.error("Failed to load issuer list");
      setIssuerOptions([]);
    }
  };

  const fetchIsinOptions = async (issuerName) => {
    try {
      const url = new URL(`${baseUrl}/master-isin/isin`);
      url.searchParams.append("issuerName", issuerName);
      const response = await apiClient.get(url.toString(), {
        headers: { ...authHeaders, "Content-Type": "application/json" },
      });
      if (response.status === 204 || response.status === 404) {
        setIsinOptions([]);
        setSelectedIsin(null);
        return;
      }
      const result = response.data;
      const list = Array.isArray(result.data) ? result.data : [];
      setIsinOptions(
        list.map((item) => ({
          isin: item.isin,
          label: `${item.isin} - ${item.companyIssuerName || ""}`.trim(),
        }))
      );
      if (!selectedIsin && list.length > 0) {
        setSelectedIsin(list[0].isin);
      }
    } catch (err) {
      console.error("Error fetching ISIN options:", err);
      message.error("Failed to load ISIN list");
      setIsinOptions([]);
      setSelectedIsin(null);
    }
  };

  const fetchCashflow = async (isin) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/bond-cash-flow/${encodeURIComponent(isin)}`,
        {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        }
      );
      const result = response.data;
      // API returns { statusCode, message, data: { isin, cashFlows: [...] } }
      const dto = result.data || {};
      setCashflowData(Array.isArray(dto.cashFlows) ? dto.cashFlows : []);
    } catch (err) {
      console.error("Error fetching cashflow:", err);
      message.error("Failed to load cashflow data");
      setCashflowData([]);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (row) => {
    setCurrentRow(row);
    if (row) {
      form.setFieldsValue({
        seriesName: row.id?.seriesName || row.seriesName,
        cashFlows: row.id?.cashFlows || row.cashFlows,
        couponDate: row.id?.couponDate || row.couponDate,
        paymentDate: row.paymentDate,
        dayCount: row.dayCount,
        noOfDaysInCouponPeriod: row.noOfDaysInCouponPeriod,
        amount: row.amount,
      });
    } else {
      form.resetFields();
    }
    setEditModalVisible(true);
  };

  const closeModals = () => {
    setEditModalVisible(false);
    setExtractedModalVisible(false);
    setCurrentRow(null);
    setExtractedRows([]);
    form.resetFields();
  };

  const closeEditModal = () => {
    // Close only the edit modal, keep extracted modal open
    setEditModalVisible(false);
    setCurrentRow(null);
    form.resetFields();
  };

  const buildPayloadRows = (rows) =>
    rows.map((row) => {
      const id = row.id || {};
      return {
        id: {
          isin: selectedIsin,
          seriesName: id.seriesName || row.seriesName,
          cashFlows: id.cashFlows || row.cashFlows,
          couponDate: id.couponDate || row.couponDate,
        },
        paymentDate: row.paymentDate,
        dayCount: row.dayCount,
        noOfDaysInCouponPeriod: row.noOfDaysInCouponPeriod,
        amount: row.amount,
      };
    });

  const handleSaveRow = async () => {
    if (!selectedIsin) {
      message.warning("Please select an ISIN first");
      return;
    }
    try {
      const values = await form.validateFields();
      const updatedRow = {
        id: {
          isin: selectedIsin,
          seriesName:
            currentRow?.id?.seriesName || currentRow?.seriesName || values.seriesName,
          cashFlows:
            currentRow?.id?.cashFlows || currentRow?.cashFlows || values.cashFlows,
          couponDate:
            currentRow?.id?.couponDate || currentRow?.couponDate || values.couponDate,
        },
        paymentDate: values.paymentDate,
        dayCount: values.dayCount,
        noOfDaysInCouponPeriod: values.noOfDaysInCouponPeriod,
        amount: values.amount,
      };

      await apiClient.put(
        `${baseUrl}/bond-cash-flow/${encodeURIComponent(selectedIsin)}`,
        updatedRow,
        {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        }
      );
      message.success("Cashflow updated successfully");
      closeModals();
      fetchCashflow(selectedIsin);
    } catch (err) {
      if (err?.errorFields) return;
      console.error("Error saving cashflow row:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to save cashflow row");
    }
  };

  const handleDeleteRow = (row) => {
    Modal.confirm({
      title: "Delete cashflow row",
      content: "Are you sure you want to delete this cashflow row?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const id = row.id || {};
          const seriesName = id.seriesName || row.seriesName;
          const cashFlows = id.cashFlows || row.cashFlows;
          const couponDate = id.couponDate || row.couponDate;

          await apiClient.delete(
            `${baseUrl}/bond-cash-flow/${encodeURIComponent(selectedIsin)}`,
            {
              headers: { ...authHeaders, "Content-Type": "application/json" },
              data: {
                id: {
                  isin: selectedIsin,
                  seriesName,
                  cashFlows,
                  couponDate,
                },
              },
            }
          );
          message.success("Cashflow row deleted successfully");
          fetchCashflow(selectedIsin);
        } catch (err) {
          console.error("Error deleting cashflow row:", err);
          const errMsg =
            err.response?.data?.message ||
            err.response?.data ||
            err.message;
          message.error(errMsg || "Failed to delete cashflow row");
        }
      },
    });
  };


  const handleUploadAndExtractPDF = () => {
    if (!selectedIsin) {
      message.warning("Please select an ISIN before uploading PDF");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setExtractionLoading(true);
      // Don't open modal until extraction is complete

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("isin", selectedIsin);

        const response = await fetch(
          `${botsApiUrl}/api/extract-bond-cashflow`,
          {
            method: "POST",
            body: formData,
          }
        );

        const result = await response.json();
        if (result.status === "success" && Array.isArray(result.data)) {
          // Map extracted data to the format expected by the table
          const mapped = result.data.map((item) => ({
            id: {
              isin: selectedIsin,
              seriesName: item.seriesName || item.series_name || "Series-I",
              cashFlows: item.cashFlows || item.cash_flows || item.type || "",
              couponDate: item.couponDate || item.coupon_date || "",
            },
            paymentDate: item.paymentDate || item.payment_date || "",
            dayCount: item.dayCount || item.day_count || "",
            noOfDaysInCouponPeriod:
              item.noOfDaysInCouponPeriod || item.no_of_days_in_coupon_period || null,
            amount: item.amount != null ? Number(item.amount) : null,
          }));
          
          // Set extracted data and show modal only after extraction is complete
          setExtractedRows(mapped);
          setExtractionLoading(false);
          setExtractedModalVisible(true);
          message.success(
            `Successfully extracted ${mapped.length} cashflow rows from PDF. Please review and save.`
          );
        } else {
          message.error(result.message || "Failed to extract bond cashflow");
          setExtractionLoading(false);
        }
      } catch (err) {
        console.error("Error extracting bond cashflow:", err);
        message.error(err.message || "Failed to extract bond cashflow");
        setExtractionLoading(false);
      }
    };
    input.click();
  };

  const handleSaveExtractedRows = async () => {
    if (!selectedIsin || extractedRows.length === 0) return;
    try {
      // Save extracted data (merged with existing data on backend)
      const payload = buildPayloadRows(extractedRows);
      
      await apiClient.post(
        `${baseUrl}/bond-cash-flow/${encodeURIComponent(selectedIsin)}`,
        payload,
        {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        }
      );
      message.success("Extracted cashflows saved successfully");
      closeModals();
      fetchCashflow(selectedIsin);
    } catch (err) {
      console.error("Error saving extracted cashflows:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data ||
        err.message;
      message.error(errMsg || "Failed to save extracted cashflows");
    }
  };

  const handleEditExtractedRow = (row, index) => {
    setCurrentRow({ ...row, _extractedIndex: index });
    form.setFieldsValue({
      seriesName: row.id?.seriesName || row.seriesName,
      cashFlows: row.id?.cashFlows || row.cashFlows,
      couponDate: row.id?.couponDate || row.couponDate,
      paymentDate: row.paymentDate,
      dayCount: row.dayCount,
      noOfDaysInCouponPeriod: row.noOfDaysInCouponPeriod,
      amount: row.amount,
    });
    setEditModalVisible(true);
  };

  const handleDeleteExtractedRow = (index) => {
    Modal.confirm({
      title: "Remove extracted row",
      content: "Are you sure you want to remove this row from the extracted data?')",
      okText: "Remove",
      okType: "danger",
      onOk: () => {
        // Only update local state, no API call
        const updated = extractedRows.filter((_, i) => i !== index);
        setExtractedRows(updated);
        message.success("Row removed from extracted data");
      },
    });
  };

  const handleSaveExtractedRow = async () => {
    if (!selectedIsin || currentRow?._extractedIndex === undefined) {
      return;
    }
    try {
      const values = await form.validateFields();
      const updatedRow = {
        id: {
          isin: selectedIsin,
          seriesName: values.seriesName,
          cashFlows: values.cashFlows,
          couponDate: values.couponDate,
        },
        paymentDate: values.paymentDate,
        dayCount: values.dayCount,
        noOfDaysInCouponPeriod: values.noOfDaysInCouponPeriod,
        amount: values.amount,
      };

      // Update extracted row in the modal (local state only, no API call)
      const updated = [...extractedRows];
      updated[currentRow._extractedIndex] = updatedRow;
      setExtractedRows(updated);
      message.success("Row updated");
      
      // Close only the edit modal, keep extracted modal open
      setEditModalVisible(false);
      setCurrentRow(null);
      form.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      console.error("Error updating extracted row:", err);
      message.error(err.message || "Failed to update extracted row");
    }
  };

  const columns = [
    {
      title: "Series",
      dataIndex: ["id", "seriesName"],
      key: "seriesName",
      render: (_, row) => row.id?.seriesName || row.seriesName,
    },
    {
      title: "Cash Flow Type",
      dataIndex: ["id", "cashFlows"],
      key: "cashFlows",
      render: (_, row) => row.id?.cashFlows || row.cashFlows,
    },
    {
      title: "Coupon Date",
      dataIndex: ["id", "couponDate"],
      key: "couponDate",
      render: (_, row) => row.id?.couponDate || row.couponDate,
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
    },
    {
      title: "Day Count Basis",
      dataIndex: "dayCount",
      key: "dayCount",
    },
    {
      title: "Days in Period",
      dataIndex: "noOfDaysInCouponPeriod",
      key: "noOfDaysInCouponPeriod",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (val) => (val != null ? Number(val).toLocaleString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(row)}
            size="small"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRow(row)}
            size="small"
          />
        </Space>
      ),
    },
  ];


  return (
    <div className="admin-page">
      <div className="admin-header-banner">
        <div className="hero-content">
          <h1 className="hero-title-news">Bond Cashflow Management</h1>
        </div>
      </div>

      <Card className="admin-main-card">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="bottom">
          <Col xs={24} md={8}>
            <label className="admin-filter-label">Issuer</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select issuer"
              value={selectedIssuer?.issuer}
              onChange={(value) => {
                const issuer = issuerOptions.find((item) => item.issuer === value);
                setSelectedIssuer(issuer || null);
              }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {issuerOptions.map((issuer) => (
                <Option key={issuer.issuer} value={issuer.issuer}>
                  {issuer.issuer}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label className="admin-filter-label">ISIN</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select ISIN"
              value={selectedIsin}
              onChange={setSelectedIsin}
              showSearch
              optionFilterProp="children"
              disabled={!selectedIssuer}
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {isinOptions.map((item) => (
                <Option key={item.isin} value={item.isin}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col
            xs={24}
            md={8}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              gap: 8,
            }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUploadAndExtractPDF}
              disabled={!selectedIsin}
              loading={extractionLoading}
            >
              Upload Cashflow PDF
            </Button>
          </Col>
        </Row>

        <Table
          className="issuer-financial-reports-table"
          columns={columns}
          dataSource={cashflowData}
          rowKey={(row) =>
            `${row.id?.isin || selectedIsin || ""}-${
              row.id?.seriesName || row.seriesName || ""
            }-${row.id?.cashFlows || row.cashFlows || ""}-${
              row.id?.couponDate || row.couponDate || ""
            }`
          }
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        open={editModalVisible}
        onCancel={currentRow?._extractedIndex !== undefined ? closeEditModal : closeModals}
        onOk={currentRow?._extractedIndex !== undefined ? handleSaveExtractedRow : handleSaveRow}
        title={currentRow ? "Edit Cashflow Row" : "Add Cashflow Row"}
        width={600}
        okText="Save"
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="seriesName"
                label="Series Name"
                rules={[{ required: true, message: "Series name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cashFlows"
                label="Cash Flow Type"
                rules={[{ required: true, message: "Cash flow type is required" }]}
              >
                <Input placeholder="Interest Payment / Redemption" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="couponDate"
                label="Coupon Date"
                rules={[{ required: true, message: "Coupon date is required" }]}
              >
                <Input placeholder="dd-MM-yyyy" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentDate" label="Payment Date">
                <Input placeholder="dd-MM-yyyy" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dayCount" label="Day Count Basis">
                <Input placeholder="e.g. Actual/365" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="noOfDaysInCouponPeriod"
                label="Days in Period"
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal for extracted cashflow rows */}
      <Modal
        open={extractedModalVisible}
        onCancel={closeModals}
        title="Extracted Cashflow Data"
        width={1200}
        okText="Save All to Database"
        onOk={handleSaveExtractedRows}
        okButtonProps={{
          disabled: extractedRows.length === 0,
        }}
      >
        {extractionLoading ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spin size="large" tip="Extracting cashflows from PDF..." />
          </div>
        ) : extractedRows.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <p>No extracted rows to display.</p>
          </div>
        ) : (
          <Table
              className="issuer-financial-reports-table"
              columns={[
                {
                  title: "Series",
                  dataIndex: ["id", "seriesName"],
                  key: "seriesName",
                  render: (_, row) => row.id?.seriesName || row.seriesName,
                },
                {
                  title: "Cash Flow Type",
                  dataIndex: ["id", "cashFlows"],
                  key: "cashFlows",
                  render: (_, row) => row.id?.cashFlows || row.cashFlows,
                },
                {
                  title: "Coupon Date",
                  dataIndex: ["id", "couponDate"],
                  key: "couponDate",
                  render: (_, row) => row.id?.couponDate || row.couponDate,
                },
                {
                  title: "Payment Date",
                  dataIndex: "paymentDate",
                  key: "paymentDate",
                },
                {
                  title: "Day Count Basis",
                  dataIndex: "dayCount",
                  key: "dayCount",
                },
                {
                  title: "Days in Period",
                  dataIndex: "noOfDaysInCouponPeriod",
                  key: "noOfDaysInCouponPeriod",
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (val) => (val != null ? Number(val).toLocaleString() : "-"),
                },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, row, index) => (
                    <Space>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditExtractedRow(row, index)}
                        size="small"
                      />
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteExtractedRow(index)}
                        size="small"
                      />
                    </Space>
                  ),
                },
              ]}
              dataSource={extractedRows}
              rowKey={(row, index) => `extracted-${index}`}
              pagination={false}
              size="small"
            />
        )}
      </Modal>
    </div>
  );
};

export default BondCashflowAdmin;


